import { useState } from "react";
import { businessId, uid } from "../inbox/businessSetup";

type Channel = "whatsapp" | "instagram" | "web";
type When = "hourly" | "message";

type Rule = {
  id: string;
  name: string;
  when: When;
  hours: string;
  channels: Channel[];
  addTags: boolean;
  moveStage: boolean;
  builtin?: boolean;
};

const CHANNELS: { id: Channel; label: string }[] = [
  { id: "whatsapp", label: "WhatsApp" },
  { id: "instagram", label: "Instagram" },
  { id: "web", label: "Web" },
];

const DEFAULTS: Rule[] = [
  {
    id: "default-hourly",
    name: "Every hour",
    when: "hourly",
    hours: "1",
    channels: ["whatsapp", "instagram", "web"],
    addTags: true,
    moveStage: true,
    builtin: true,
  },
];

const EMPTY_DRAFT: Omit<Rule, "id"> = {
  name: "",
  when: "hourly",
  hours: "1",
  channels: ["whatsapp", "instagram", "web"],
  addTags: true,
  moveStage: true,
};

const STORAGE_KEY = "fd24.smartTriggers.v2";

function loadRules(): Rule[] {
  try {
    const all = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
    const rules = all[businessId()];
    return Array.isArray(rules) ? rules : [];
  } catch {
    return [];
  }
}

function saveRules(rules: Rule[]) {
  const all = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
  all[businessId()] = rules;
  localStorage.setItem(STORAGE_KEY, JSON.stringify(all));
}

function channelList(channels: Channel[], join: "and" | "or" = "and"): string {
  const labels = CHANNELS.filter((channel) => channels.includes(channel.id)).map((channel) => channel.label);
  if (labels.length === 0) return "no channel";
  if (labels.length === 1) return labels[0];
  if (labels.length === 2) return `${labels[0]} ${join} ${labels[1]}`;
  return `${labels.slice(0, -1).join(", ")}, ${join} ${labels[labels.length - 1]}`;
}

function triggerSentence(rule: Rule): string {
  if (rule.when === "message") {
    return `A new ${channelList(rule.channels, "or")} message arrives`;
  }
  const hours = Number(rule.hours) || 1;
  return `Every ${hours} hour${hours === 1 ? "" : "s"}, for each lead`;
}

function actionSentence(rule: Rule): string {
  if (rule.addTags && rule.moveStage) return "Add or remove tags and move stages based on messages";
  if (rule.addTags) return "Add or remove tags based on messages";
  if (rule.moveStage) return "Move stages based on messages";
  return "No action";
}

function ready(draft: Omit<Rule, "id">): boolean {
  return Boolean(draft.name.trim()) && draft.channels.length > 0 && (draft.addTags || draft.moveStage);
}

export default function SmartTriggersPage() {
  const [rules, setRules] = useState<Rule[]>(loadRules);
  const [query, setQuery] = useState("");
  const [draft, setDraft] = useState<Omit<Rule, "id"> | null>(null);

  const all = DEFAULTS.concat(rules);
  const visible = all.filter((rule) => {
    const haystack = `${rule.name} ${triggerSentence(rule)} ${actionSentence(rule)}`.toLowerCase();
    return haystack.includes(query.trim().toLowerCase());
  });

  const persist = (next: Rule[]) => {
    saveRules(next);
    setRules(next);
  };

  const addRule = () => {
    if (!draft || !ready(draft)) return;
    persist(rules.concat({ ...draft, id: uid(), name: draft.name.trim() }));
    setDraft(null);
  };

  const toggleChannel = (channel: Channel) => {
    if (!draft) return;
    const channels = draft.channels.includes(channel)
      ? draft.channels.filter((item) => item !== channel)
      : draft.channels.concat(channel);
    setDraft({ ...draft, channels });
  };

  return (
    <div className="mx-auto max-w-6xl py-4">
      <div className="mb-4 flex items-center gap-3">
        <h2 className="text-2xl font-semibold text-gray-900">Smart Triggers</h2>
        <button
          type="button"
          onClick={() => setDraft({ ...EMPTY_DRAFT })}
          className="ml-auto rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white"
        >
          Add rule
        </button>
      </div>

      <input
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        placeholder="Search for any rule"
        className="mb-4 w-full rounded-lg border border-gray-200 px-4 py-2.5 text-sm"
      />

      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
        <div className="grid grid-cols-[1.1fr_1.6fr_1.3fr_auto] bg-gray-50 px-4 py-3 text-sm text-gray-500">
          <span>Rule Name</span>
          <span>Trigger</span>
          <span>Action</span>
          <span />
        </div>
        {visible.length === 0 ? (
          <p className="px-4 py-8 text-sm text-gray-500">No rules match this search.</p>
        ) : (
          visible.map((rule) => (
            <div key={rule.id} className="grid grid-cols-[1.1fr_1.6fr_1.3fr_auto] items-center gap-3 border-t border-gray-100 px-4 py-4 text-sm">
              <span className="truncate font-medium text-gray-800" title={rule.name}>{rule.name}</span>
              <span className="truncate text-gray-600" title={triggerSentence(rule)}>{triggerSentence(rule)}</span>
              <span className="truncate text-gray-600" title={actionSentence(rule)}>{actionSentence(rule)}</span>
              {rule.builtin ? (
                <span className="text-xs text-gray-400">Default</span>
              ) : (
                <button
                  type="button"
                  className="text-xs text-red-600"
                  onClick={() => persist(rules.filter((item) => item.id !== rule.id))}
                >
                  Remove
                </button>
              )}
            </div>
          ))
        )}
      </div>

      {draft ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-lg rounded-xl bg-white p-5 shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900">New rule</h3>
            <p className="mt-1 text-sm text-gray-500">
              Reads the messages, then adds or removes tags and moves the stage.
            </p>
            <label className="mt-4 block text-sm text-gray-600">
              Rule name
              <input
                value={draft.name}
                onChange={(event) => setDraft({ ...draft, name: event.target.value })}
                className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
              />
            </label>
            <label className="mt-3 block text-sm text-gray-600">
              Trigger
              <select
                value={draft.when}
                onChange={(event) => setDraft({ ...draft, when: event.target.value as When })}
                className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
              >
                <option value="hourly">Every hour, for each lead</option>
                <option value="message">When a WhatsApp, Instagram, or web message arrives</option>
              </select>
            </label>
            {draft.when === "hourly" ? (
              <label className="mt-3 block text-sm text-gray-600">
                Hours
                <input
                  type="number"
                  min={1}
                  value={draft.hours}
                  onChange={(event) => setDraft({ ...draft, hours: event.target.value })}
                  className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                />
              </label>
            ) : null}
            <fieldset className="mt-3">
              <legend className="text-sm text-gray-600">Channels</legend>
              <div className="mt-2 flex gap-2">
                {CHANNELS.map((channel) => {
                  const on = draft.channels.includes(channel.id);
                  return (
                    <button
                      key={channel.id}
                      type="button"
                      onClick={() => toggleChannel(channel.id)}
                      className={`rounded-full px-3 py-1 text-xs font-medium ${on ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-600"}`}
                    >
                      {channel.label}
                    </button>
                  );
                })}
              </div>
            </fieldset>
            <fieldset className="mt-3">
              <legend className="text-sm text-gray-600">Action</legend>
              <label className="mt-2 flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={draft.addTags}
                  onChange={(event) => setDraft({ ...draft, addTags: event.target.checked })}
                />
                Add or remove tags
              </label>
              <label className="mt-2 flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={draft.moveStage}
                  onChange={(event) => setDraft({ ...draft, moveStage: event.target.checked })}
                />
                Move stage
              </label>
            </fieldset>
            <div className="mt-5 flex justify-end gap-2">
              <button type="button" onClick={() => setDraft(null)} className="rounded-lg px-4 py-2 text-sm text-gray-600">
                Cancel
              </button>
              <button
                type="button"
                onClick={addRule}
                disabled={!ready(draft)}
                className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white disabled:bg-gray-200"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
